import React, { useState, useEffect, useRef } from 'react';
import { get, post, put } from '../../utils/apiClient';
import { clearCacheKey } from '../../utils/cacheUtils';
import Base64Image from '../Base64Image';
import {
  AdminSectionTitle,
  AdminSectionText,
  AdminLoginLabel,
  AdminLoginInput,
  AdminLoginButton,
  AdminButton,
  AdminSaveButton,
} from '../../styles/AdminPageStyles';

const AdminRehabPage = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [uploadingHeader, setUploadingHeader] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const [newGalleryItem, setNewGalleryItem] = useState({
    title: '',
    description: '',
    albumName: '',
    category: '',
    displayOrder: '',
    file: null,
    files: [],
  });
  const rehabGalleryFileInputRef = useRef(null);

  useEffect(() => {
    const loadPage = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await get('/rehab');
        setPageData(data);
      } catch (err) {
        setError(err && err.message ? err.message : 'Failed to load rehab page.');
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, []);

  const handleHeaderBackgroundUpload = async (file) => {
    if (!file) return;
    setUploadingHeader(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('image', file);
      await post('/rehab/header/background-image', formData);
      const data = await get('/rehab');
      setPageData(data);
      clearCacheKey('rehab_data');
    } catch (err) {
      setUploadError(err && err.message ? err.message : 'Failed to upload header background.');
    } finally {
      setUploadingHeader(false);
    }
  };

  const handleSimpleFieldChange = (field, value) => {
    setPageData((prev) => ({
      ...(prev || {}),
      [field]: value,
    }));
  };

  const updateGalleryItem = (index, field, value) => {
    setPageData((prev) => {
      const next = { ...(prev || {}) };
      const gallery = Array.isArray(next.galleryImages) ? [...next.galleryImages] : [];
      const existing = gallery[index] || {};
      gallery[index] = {
        ...existing,
        [field]: value,
      };
      next.galleryImages = gallery;
      return next;
    });
  };

  const addGalleryItem = () => {
    setPageData((prev) => {
      const next = { ...(prev || {}) };
      const gallery = Array.isArray(next.galleryImages) ? [...next.galleryImages] : [];
      gallery.push({ title: '', description: '' });
      next.galleryImages = gallery;
      return next;
    });
  };

  const removeGalleryItem = (index) => {
    setPageData((prev) => {
      if (!prev || !Array.isArray(prev.galleryImages)) return prev;
      const gallery = prev.galleryImages.filter((_, i) => i !== index);
      return {
        ...prev,
        galleryImages: gallery,
      };
    });
  };

  const handleNewGalleryFieldChange = (field, value) => {
    setNewGalleryItem((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNewGalleryFileChange = (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setNewGalleryItem((prev) => ({
      ...prev,
      file: files[0] || null,
      files,
    }));
  };

  const handleGalleryUpload = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    const filesToUpload =
      newGalleryItem.files && newGalleryItem.files.length > 0
        ? newGalleryItem.files
        : newGalleryItem.file
        ? [newGalleryItem.file]
        : [];

    if (filesToUpload.length === 0) {
      setUploadError('Please select at least one image file for the gallery.');
      return;
    }

    setUploadingGallery(true);
    setUploadError(null);

    try {
      for (const file of filesToUpload) {
        const formData = new FormData();
        formData.append('image', file);
        if (newGalleryItem.title) formData.append('title', newGalleryItem.title);
        if (newGalleryItem.description) formData.append('description', newGalleryItem.description);
        if (newGalleryItem.albumName) formData.append('albumName', newGalleryItem.albumName);
        if (newGalleryItem.category) formData.append('category', newGalleryItem.category);
        if (newGalleryItem.displayOrder !== '') {
          formData.append('displayOrder', newGalleryItem.displayOrder);
        }

        await post('/rehab/gallery/upload', formData);
      }
      const data = await get('/rehab');
      setPageData(data);
      setNewGalleryItem({
        title: '',
        description: '',
        albumName: '',
        category: '',
        displayOrder: '',
        file: null,
        files: [],
      });
      clearCacheKey('rehab_data');
    } catch (err) {
      setUploadError(err && err.message ? err.message : 'Failed to upload gallery image.');
    } finally {
      setUploadingGallery(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!pageData) return;

    setSaving(true);
    setError(null);
    setSaveSuccess(null);

    try {
      const payload = {
        header: {
          ...(pageData.header || {}),
          title:
            (pageData.header && pageData.header.title) ||
            'Addict to Disciple Program',
        },
        overviewText: pageData.overviewText || '',
        startDate: pageData.startDate || '',
        motivationText: pageData.motivationText || '',
        trainingText: pageData.trainingText || '',
        resultsText: pageData.resultsText || '',
        galleryImages: Array.isArray(pageData.galleryImages) ? pageData.galleryImages : [],
      };

      const updated = await put('/rehab', payload);
      setPageData(updated);
      setSaveSuccess('Rehab page saved successfully.');
      clearCacheKey('rehab_data');
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to save rehab page.');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !pageData) {
    return (
      <>
        <AdminSectionTitle>Rehab Program CMS</AdminSectionTitle>
        <AdminSectionText>Loading rehab page...</AdminSectionText>
      </>
    );
  }

  const header = pageData && pageData.header ? pageData.header : null;

  return (
    <>
      <AdminSectionTitle>Rehab Program CMS</AdminSectionTitle>

      <form onSubmit={handleSave}>
        <div style={{ marginBottom: 16 }}>
          <AdminSectionTitle style={{ fontSize: '1rem', marginBottom: 8 }}>
            Header Background Image
          </AdminSectionTitle>
          {header && header.backgroundImage && (
            <div style={{ marginBottom: 8 }}>
              <Base64Image
                image={header.backgroundImage}
                alt={header.title || 'Rehab header'}
                style={{ width: 260, borderRadius: 6 }}
              />
            </div>
          )}
          <AdminLoginLabel htmlFor="rehab-header-bg">Upload Header Background</AdminLoginLabel>
          <input
            id="rehab-header-bg"
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
              if (file) {
                handleHeaderBackgroundUpload(file);
                e.target.value = '';
              }
            }}
            style={{ fontSize: '0.9rem' }}
          />
          {uploadingHeader && (
            <AdminSectionText style={{ fontSize: '0.85rem' }}>
              Uploading header background...
            </AdminSectionText>
          )}
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminLoginLabel htmlFor="overviewText">Overview Text</AdminLoginLabel>
          <textarea
            id="overviewText"
            rows={3}
            value={(pageData && pageData.overviewText) || ''}
            onChange={(e) => handleSimpleFieldChange('overviewText', e.target.value)}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminLoginLabel htmlFor="startDate">Program Start Date</AdminLoginLabel>
          <AdminLoginInput
            id="startDate"
            value={(pageData && pageData.startDate) || ''}
            onChange={(e) => handleSimpleFieldChange('startDate', e.target.value)}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminLoginLabel htmlFor="motivationText">Motivation Text</AdminLoginLabel>
          <textarea
            id="motivationText"
            rows={3}
            value={(pageData && pageData.motivationText) || ''}
            onChange={(e) => handleSimpleFieldChange('motivationText', e.target.value)}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminLoginLabel htmlFor="trainingText">Training Text</AdminLoginLabel>
          <textarea
            id="trainingText"
            rows={3}
            value={(pageData && pageData.trainingText) || ''}
            onChange={(e) => handleSimpleFieldChange('trainingText', e.target.value)}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminLoginLabel htmlFor="resultsText">Challenges Text</AdminLoginLabel>
          <textarea
            id="resultsText"
            rows={3}
            value={(pageData && pageData.resultsText) || ''}
            onChange={(e) => handleSimpleFieldChange('resultsText', e.target.value)}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
          />
        </div>

        <hr style={{ margin: '16px 0' }} />

        <AdminSectionTitle>Gallery</AdminSectionTitle>
        <AdminSectionText style={{ marginBottom: 8 }}>
          Manage the rehab gallery entries (title, description, and display order).
        </AdminSectionText>

        {pageData && Array.isArray(pageData.galleryImages) && pageData.galleryImages.map((item, index) => (
          <div
            key={index}
            style={{
              border: '1px solid #ddd',
              borderRadius: 8,
              padding: 10,
              marginBottom: 8,
            }}
          >
            {item.image && (
              <div style={{ marginBottom: 8 }}>
                <Base64Image
                  image={item.image}
                  alt={item.title || 'Rehab gallery image'}
                  style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 4 }}
                />
              </div>
            )}
            <div style={{ marginBottom: 8 }}>
              <AdminLoginLabel htmlFor={`gallery-title-${index}`}>
                Title
              </AdminLoginLabel>
              <AdminLoginInput
                id={`gallery-title-${index}`}
                value={item.title || ''}
                onChange={(e) => updateGalleryItem(index, 'title', e.target.value)}
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <AdminLoginLabel htmlFor={`gallery-description-${index}`}>
                Description
              </AdminLoginLabel>
              <textarea
                id={`gallery-description-${index}`}
                rows={3}
                value={item.description || ''}
                onChange={(e) => updateGalleryItem(index, 'description', e.target.value)}
                style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <AdminLoginLabel htmlFor={`gallery-order-${index}`}>
                Display Order (optional)
              </AdminLoginLabel>
              <AdminLoginInput
                id={`gallery-order-${index}`}
                type="number"
                value={
                  typeof item.displayOrder === 'number'
                    ? item.displayOrder
                    : ''
                }
                onChange={(e) => updateGalleryItem(index, 'displayOrder', e.target.value === '' ? undefined : Number(e.target.value))}
              />
            </div>
            <AdminButton type="button" onClick={() => removeGalleryItem(index)}>
              Remove Gallery Item
            </AdminButton>
          </div>
        ))}

        <div style={{ marginBottom: 24 }}>
          <AdminButton type="button" onClick={addGalleryItem}>
            Add Gallery Item
          </AdminButton>
        </div>

        <div style={{ marginBottom: 24 }}>
          <AdminSectionTitle style={{ fontSize: '0.95rem', marginBottom: 8 }}>
            Add Gallery Image
          </AdminSectionTitle>
          <div style={{ marginBottom: 8 }}>
            <AdminLoginLabel htmlFor="new-gallery-title">Title (optional)</AdminLoginLabel>
            <AdminLoginInput
              id="new-gallery-title"
              value={newGalleryItem.title}
              onChange={(e) => handleNewGalleryFieldChange('title', e.target.value)}
            />
          </div>
          <div style={{ marginBottom: 8 }}>
            <AdminLoginLabel htmlFor="new-gallery-description">Description (optional)</AdminLoginLabel>
            <textarea
              id="new-gallery-description"
              rows={2}
              value={newGalleryItem.description}
              onChange={(e) => handleNewGalleryFieldChange('description', e.target.value)}
              style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
            />
          </div>
          <div style={{ marginBottom: 8 }}>
            <AdminLoginLabel htmlFor="new-gallery-album">Album Name (optional)</AdminLoginLabel>
            <AdminLoginInput
              id="new-gallery-album"
              value={newGalleryItem.albumName}
              onChange={(e) => handleNewGalleryFieldChange('albumName', e.target.value)}
            />
          </div>
          <div style={{ marginBottom: 8 }}>
            <AdminLoginLabel htmlFor="new-gallery-category">Category (optional)</AdminLoginLabel>
            <AdminLoginInput
              id="new-gallery-category"
              value={newGalleryItem.category}
              onChange={(e) => handleNewGalleryFieldChange('category', e.target.value)}
            />
          </div>
          <div style={{ marginBottom: 8 }}>
            <AdminLoginLabel htmlFor="new-gallery-order">Display Order (optional)</AdminLoginLabel>
            <AdminLoginInput
              id="new-gallery-order"
              type="number"
              value={newGalleryItem.displayOrder}
              onChange={(e) => handleNewGalleryFieldChange('displayOrder', e.target.value)}
            />
          </div>
          <div style={{ marginBottom: 8 }}>
            <AdminLoginLabel htmlFor="new-gallery-file">Image File(s)</AdminLoginLabel>
            <input
              id="new-gallery-file"
              type="file"
              accept="image/*"
              multiple
              onChange={handleNewGalleryFileChange}
              ref={rehabGalleryFileInputRef}
              style={{ fontSize: '0.9rem' }}
            />
          </div>
          <AdminLoginButton
            type="button"
            onClick={handleGalleryUpload}
            disabled={uploadingGallery}
          >
            {uploadingGallery ? 'Uploading...' : 'Upload Gallery Image'}
          </AdminLoginButton>
        </div>

        <div style={{ marginTop: 32, display: 'flex', alignItems: 'center', gap: '16px' }}>
          <AdminSaveButton type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save Rehab Page'}
          </AdminSaveButton>
          {saveSuccess && (
            <span style={{ color: '#1b5e20', fontWeight: '500' }}>{saveSuccess}</span>
          )}
          {error && (
            <span style={{ color: '#b71c1c', fontWeight: '500' }}>{error}</span>
          )}
          {uploadError && (
            <span style={{ color: '#b71c1c', fontWeight: '500' }}>{uploadError}</span>
          )}
        </div>
      </form>
    </>
  );
};

export default AdminRehabPage;
